import React, { useMemo, useRef, useEffect, forwardRef } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, Environment, Html } from '@react-three/drei';
import * as THREE from 'three';
import { SpringConfig } from './Configurator';

interface SpringPreviewProps {
  config: SpringConfig;
  diameterProfile?: number[]; // New prop for variable diameter
  baseHookDiameter?: number; // Add this prop for fixed hook diameter
}

const Spring = forwardRef<THREE.Group, { config: SpringConfig; diameterProfile?: number[] }>(({ config, diameterProfile }, ref) => {
  // Calculate spring end points for proper hook/circle connection
  const springEndPoints = useMemo(() => {
    const coils = config.springIndex;
    const height = config.length;
    let profile = diameterProfile && diameterProfile.length > 1 ? diameterProfile : [config.diameter];
    
    function getRadius(t: number) {
      if (profile.length === 1) return profile[0] / 2;
      const idx = t * (profile.length - 1);
      const i0 = Math.floor(idx);
      const i1 = Math.min(profile.length - 1, i0 + 1);
      const frac = idx - i0;
      return ((1 - frac) * profile[i0] + frac * profile[i1]) / 2;
    }

    // Calculate start point (t=0)
    const startRadius = getRadius(0);
    const startAngle = 0;
    const startPoint = new THREE.Vector3(
      Math.cos(startAngle) * startRadius,
      -height / 2,
      Math.sin(startAngle) * startRadius
    );

    // Calculate end point (t=1)
    const endRadius = getRadius(1);
    const endAngle = coils * Math.PI * 2;
    const endPoint = new THREE.Vector3(
      Math.cos(endAngle) * endRadius,
      height / 2,
      Math.sin(endAngle) * endRadius
    );

    return { start: startPoint, end: endPoint };
  }, [config.length, config.diameter, config.springIndex, diameterProfile]);

  const springGeometry = useMemo(() => {
    const points: THREE.Vector3[] = [];
    const coils = config.springIndex; // Use springIndex for coil count
    const height = config.length;
    const coilHeight = height / coils;
    const segments = coils * 20;
    // Use diameterProfile if provided, else fallback to constant
    let profile = diameterProfile && diameterProfile.length > 1 ? diameterProfile : [config.diameter];
    // Interpolate diameter for each segment
    function getRadius(t: number) {
      if (profile.length === 1) return profile[0] / 2;
      const idx = t * (profile.length - 1);
      const i0 = Math.floor(idx);
      const i1 = Math.min(profile.length - 1, i0 + 1);
      const frac = idx - i0;
      return ((1 - frac) * profile[i0] + frac * profile[i1]) / 2;
    }
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const angle = t * coils * Math.PI * 2;
      const y = t * height - height / 2;
      const radius = getRadius(t);
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      points.push(new THREE.Vector3(x, y, z));
    }
    const curve = new THREE.CatmullRomCurve3(points);
    const tubeGeometry = new THREE.TubeGeometry(curve, segments, config.thickness / 20, 16, false);
    return tubeGeometry;
  }, [config.length, config.diameter, config.thickness, config.springIndex, diameterProfile]);

  const hookGeometry = useMemo(() => {
    const hookPoints: THREE.Vector3[] = [];
    // Use baseHookDiameter if provided, otherwise fall back to config.diameter
    const hookRadius = (config.baseHookDiameter || config.diameter) / 2;
    
    // Get the spring diameter at the endpoints to ensure proper connection
    let profile = diameterProfile && diameterProfile.length > 1 ? diameterProfile : [config.diameter];
    const startSpringRadius = profile[0] / 2;
    const endSpringRadius = profile[profile.length - 1] / 2;
    const maxEndRadius = Math.max(startSpringRadius, endSpringRadius);
    
    // Start from the spring's outer edge (extend inward to center, then outward for hook)
    hookPoints.push(new THREE.Vector3(-maxEndRadius, 0, 0)); // Start from spring edge
    hookPoints.push(new THREE.Vector3(0, 0, 0)); // Go to center
    
    const arcPoints = 40;
    for (let i = 0; i <= arcPoints; i++) {
      const angle = (i / arcPoints) * Math.PI; // 180° arc in XY plane
      const x = Math.cos(angle) * hookRadius;
      const y = Math.sin(angle) * hookRadius;
      hookPoints.push(new THREE.Vector3(x, y, 0));
    }
    const hookCurve = new THREE.CatmullRomCurve3(hookPoints);
    return new THREE.TubeGeometry(hookCurve, hookPoints.length * 2, config.thickness / 20, 16, false);
  }, [config.baseHookDiameter, config.diameter, config.thickness, diameterProfile]);

  // Create separate circle geometries for start and end
  const startCircleGeometry = useMemo(() => {
    let profile = diameterProfile && diameterProfile.length > 1 ? diameterProfile : [config.diameter];
    const startSpringRadius = profile[0] / 2;
    const circleRadius = startSpringRadius; // Use the actual spring diameter at the start
    
    const circlePoints: THREE.Vector3[] = [];
    
    // Start from the spring's outer edge and extend to form circle
    circlePoints.push(new THREE.Vector3(-startSpringRadius, 0, 0)); // Start from spring edge
    circlePoints.push(new THREE.Vector3(0, 0, 0)); // Go to center
    
    const circleSegments = 64;
    for (let i = 0; i <= circleSegments; i++) {
      const angle = (i / circleSegments) * Math.PI * 2;
      const x = Math.cos(angle) * circleRadius;
      const y = Math.sin(angle) * circleRadius;
      circlePoints.push(new THREE.Vector3(x, y, 0));
    }
    const circleCurve = new THREE.CatmullRomCurve3(circlePoints, true);
    return new THREE.TubeGeometry(circleCurve, circlePoints.length * 2, config.thickness / 20, 16, true);
  }, [config.diameter, config.thickness, diameterProfile]);

  const endCircleGeometry = useMemo(() => {
    let profile = diameterProfile && diameterProfile.length > 1 ? diameterProfile : [config.diameter];
    const endSpringRadius = profile[profile.length - 1] / 2;
    const circleRadius = endSpringRadius; // Use the actual spring diameter at the end
    
    const circlePoints: THREE.Vector3[] = [];
    
    // Start from the spring's outer edge and extend to form circle
    circlePoints.push(new THREE.Vector3(-endSpringRadius, 0, 0)); // Start from spring edge
    circlePoints.push(new THREE.Vector3(0, 0, 0)); // Go to center
    
    const circleSegments = 64;
    for (let i = 0; i <= circleSegments; i++) {
      const angle = (i / circleSegments) * Math.PI * 2;
      const x = Math.cos(angle) * circleRadius;
      const y = Math.sin(angle) * circleRadius;
      circlePoints.push(new THREE.Vector3(x, y, 0));
    }
    const circleCurve = new THREE.CatmullRomCurve3(circlePoints, true);
    return new THREE.TubeGeometry(circleCurve, circlePoints.length * 2, config.thickness / 20, 16, true);
  }, [config.diameter, config.thickness, diameterProfile]);

  // Material based on steel type
  const springMaterial = useMemo(() => {
    const materialProps = {
      metalness: 0.8,
      roughness: 0.2,
    };
    
    switch (config.steelType) {
      case 'stainless':
        return new THREE.MeshStandardMaterial({ ...materialProps, color: '#c0c0c0' });
      case 'alloy':
        return new THREE.MeshStandardMaterial({ ...materialProps, color: '#8b7355' });
      default: // carbon
        return new THREE.MeshStandardMaterial({ ...materialProps, color: '#404040' });
    }
  }, [config.steelType]);

  const hookMaterial = useMemo(() => {
    const baseMaterial = springMaterial.clone();
    // Make hooks slightly brighter for visibility
    baseMaterial.emissive = new THREE.Color(0x111111);
    return baseMaterial;
  }, [springMaterial]);

  const groupRef = useRef<THREE.Group>(null);

  // Anti-gravity floating and slow spinning effect
  useFrame(({ clock }) => {
    if (groupRef.current) {
      const t = clock.getElapsedTime();
      groupRef.current.position.y = 0.3 + Math.sin(t * 1.2) * 0.15; // anti-gravity float
      groupRef.current.rotation.y = t * 0.25; // slow spin
    }
  });

  return (
    <group ref={node => {
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<THREE.Group | null>).current = node;
      groupRef.current = node;
    }} name="spring-assembly">
      {/* Main spring body */}
      <mesh geometry={springGeometry} material={springMaterial} />
      
      {/* Left end (bottom) */}
      {config.leftHook ? (
        <group position={[0, springEndPoints.start.y, 0]}>
          <mesh geometry={hookGeometry} material={hookMaterial} rotation={[Math.PI, Math.PI, 0]} />
        </group>
      ) : (
        <group position={[0, springEndPoints.start.y, 0]}>
          <mesh geometry={startCircleGeometry} material={hookMaterial} rotation={[Math.PI / 2, 0, 0]} />
        </group>
      )}

      {/* Right end (top) */}
      {config.rightHook ? (
        <group position={[0, springEndPoints.end.y, 0]}>
          <mesh geometry={hookGeometry} material={hookMaterial} rotation={[0, Math.PI, 0]} />
        </group>
      ) : (
        <group position={[0, springEndPoints.end.y, 0]}>
          <mesh geometry={endCircleGeometry} material={hookMaterial} rotation={[Math.PI / 2, 0, 0]} />
        </group>
      )}
    </group>
  );
});

const DimensionLines: React.FC<{ config: SpringConfig }> = ({ config }) => {
  const color = new THREE.Color("#ff3366");
  const lineWidth = 2;

  const tooltipStyle = {
    background: 'rgba(211, 211, 211, 0.8)',
    padding: '4px 8px', 
    borderRadius: '4px', 
    color: '#333',
    fontSize: '12px',
    fontFamily: 'sans-serif'
  };

  return (
    <group>
      {/* Diameter dimension */}
      <group position={[0, 0, config.diameter / 2]}>
        <line>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              args={[new Float32Array([-config.diameter/2, 0, 0, config.diameter/2, 0, 0]), 3]}
            />
          </bufferGeometry>
          <lineBasicMaterial color={color} linewidth={lineWidth} />
        </line>
        <Html position={[0, 0.2, 0]} center>
          <div style={tooltipStyle}>
            Ø{config.diameter.toFixed(1)}mm
          </div>
        </Html>
      </group>

      {/* Length dimension */}
      <group position={[config.diameter/2 + 1, 0, 0]} rotation={[0, -Math.PI/2, 0]}>
        <line>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              args={[new Float32Array([0, -config.length/2, 0, 0, config.length/2, 0]), 3]}
            />
          </bufferGeometry>
          <lineBasicMaterial color={color} linewidth={lineWidth} />
        </line>
        <Html position={[0, 0, 0]} center>
          <div style={tooltipStyle}>
            {config.length.toFixed(1)}mm
          </div>
        </Html>
      </group>

    </group>
  );
};

const SpringPreview: React.FC<SpringPreviewProps> = ({ config, diameterProfile }) => {
  const springRef = useRef<THREE.Group>(null);
  const controlsRef = useRef<any>(null);

  // Calculate adaptive distance limits based on spring size
  const springSize = useMemo(() => {
    const maxDim = Math.max(config.length, config.diameter);
    const hookSize = config.diameter; // Hooks add to the overall size
    return maxDim + hookSize;
  }, [config.length, config.diameter]);

  // Calculate the default camera distance based on spring parameters
  const defaultCameraDistance = useMemo(() => {
    // Simulate the FitCamera logic
    const maxDim = Math.max(config.length, config.diameter);
    const paddingFactor = 1.5;
    const fov = 50; // Default fov from Canvas
    const fovRad = THREE.MathUtils.degToRad(fov);
    let distance = (maxDim / 2) / Math.tan(fovRad / 2) * paddingFactor;
    const minDistance = 8;
    return Math.max(distance, minDistance);
  }, [config.length, config.diameter]);

  const FitCamera: React.FC = () => {
    const { camera } = useThree();

    useEffect(() => {
      if (!springRef.current) return;

      // Calculate bounding box only for the spring assembly, excluding dimension lines
      const springAssembly = springRef.current.getObjectByName('spring-assembly');
      const targetObject = springAssembly || springRef.current;
      
      const box = new THREE.Box3().setFromObject(targetObject);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());

      // Get the maximum dimension and add generous padding
      const maxDim = Math.max(size.x, size.y, size.z);
      let distance;
      
      if (camera instanceof THREE.PerspectiveCamera) {
        const fov = THREE.MathUtils.degToRad(camera.fov);
        // Reduce padding factor for more zoomed view while keeping ends visible
        const paddingFactor = 1.5; // Reduced for closer view
        distance = (maxDim / 2) / Math.tan(fov / 2) * paddingFactor;
        
        // Set minimum distance to prevent issues with very small springs
        const minDistance = 8;
        distance = Math.max(distance, minDistance);
      } else {
        // For OrthographicCamera or other camera types
        distance = maxDim * 3; // Increased padding for orthographic
      }

      const phi = THREE.MathUtils.degToRad(90 - 25); // 25 degree tilt up
      const theta = THREE.MathUtils.degToRad(45); // view from an angle

      camera.position.setFromSphericalCoords(distance, phi, theta);
      camera.lookAt(center);

      if (controlsRef.current) {
        controlsRef.current.target.copy(center);
        controlsRef.current.update();
      }
    }, [camera, config]);

    return null;
  };

  return (
    <div className="spring-preview" style={{ backgroundColor: '#f5f5dc' }}>
      <Canvas camera={{ position: [0, 0, 10], fov: 50 }}>
        <ambientLight intensity={0.4} />
        <directionalLight position={[10, 10, 5]} intensity={1} />
        <pointLight position={[-10, -10, -5]} intensity={0.5} />

        <Spring ref={springRef} config={config} diameterProfile={diameterProfile} />
        <DimensionLines config={config} />

        <OrbitControls
          ref={controlsRef}
          enablePan={true}
          enableZoom={true}
          enableRotate={true}
          minDistance={springSize * 0.5}
          maxDistance={defaultCameraDistance}
        />

        <Environment preset="studio" />
        <FitCamera />
      </Canvas>
    </div>
  );
};

export default SpringPreview;
